import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import RateReviewIcon from "@mui/icons-material/RateReview";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import KeywordBox from "../components/KeywordBox";
import PreviewRefreshJobDialog from "../pzl_common/PreviewRefreshJobDialog";
import {
    adminTagFilterAutocompleteSlotProps,
    adminTagFilterChipSx,
    filterAdminTagOptions,
} from "../pzl_common/board/adminTagFilterChipSx";
import BatchPublishReviewedDialog from "../pzl_common/BatchPublishReviewedDialog";
import { getStoryTemplateSummary } from "./storyDisplayUtils";
import AdminSlidingService from "./AdminSlidingService";
import AdminStoryService from "./AdminStoryService";
import StoryImageZoomDialog from "./StoryImageZoomDialog";
import SlidingPlaySummaryRow from "./play/SlidingPlaySummaryRow";
import { DIFF_LEVELS, getStoryFreeCheckboxState, templateForGridAndLevel } from "./adminSlidingUi";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;
const SLIDING_STATE_STORAGE_KEY = "admin.sliding.browse.state";
const SLIDING_PAGE_SIZE_STORAGE_KEY = "admin.sliding.browse.pageSize";
const SLIDING_LANGUAGE_STORAGE_KEY = "admin.sliding.browse.language";
const SLIDING_TAGS_STORAGE_KEY = "admin.sliding.browse.tags";
const SLIDING_KEYWORD_STORAGE_KEY = "admin.sliding.browse.keyword";
const SLIDING_FREE_STORAGE_KEY = "admin.sliding.browse.free";
const SLIDING_STANDALONE_STORAGE_KEY = "admin.sliding.browse.standaloneImage";
const SLIDING_VIEW_STORAGE_KEY = "admin.sliding.browse.view";
const SLIDING_SORT_STORAGE_KEY = "admin.sliding.browse.sort";
const SLIDING_BROWSE_TAG_STORAGE_KEY = "admin.sliding.browse.browseTag";

const ORDER_BY_OPTIONS = [
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];

const VALID_SORT_VALUES = new Set(ORDER_BY_OPTIONS.map((o) => o.value));
const DEFAULT_SORT = "publishedAt:desc";

const FREE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Free" },
    { value: "false", label: "Paid" },
];

const STANDALONE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Standalone" },
    { value: "false", label: "Not standalone" },
];

const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

function loadSlidingStateFilter() {
    try {
        const v = localStorage.getItem(SLIDING_STATE_STORAGE_KEY);
        if (v === "PUBLISHED" || v === "REVIEWED" || v === "DRAFT" || v === "") return v;
    } catch {}
    return "PUBLISHED";
}

function loadSlidingLanguageFilter() {
    try {
        const v = localStorage.getItem(SLIDING_LANGUAGE_STORAGE_KEY);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {}
    return "";
}

function loadSlidingFreeFilter() {
    try {
        const v = localStorage.getItem(SLIDING_FREE_STORAGE_KEY);
        if (v === "" || v === "true" || v === "false") return v;
    } catch {}
    return "";
}

function loadSlidingStandaloneFilter() {
    try {
        const v = localStorage.getItem(SLIDING_STANDALONE_STORAGE_KEY);
        if (v === "" || v === "true" || v === "false") return v;
    } catch {}
    return "";
}

function loadSlidingViewMode() {
    try {
        const v = localStorage.getItem(SLIDING_VIEW_STORAGE_KEY);
        if (v === "list" || v === "image" || v === "tags") return v;
    } catch {}
    return "list";
}

function loadSlidingBrowseTag() {
    try {
        const v = localStorage.getItem(SLIDING_BROWSE_TAG_STORAGE_KEY);
        return typeof v === "string" ? v.trim() : "";
    } catch {}
    return "";
}

function loadSlidingPageSize() {
    try {
        const n = parseInt(localStorage.getItem(SLIDING_PAGE_SIZE_STORAGE_KEY), 10);
        if (PAGE_SIZE_OPTIONS.includes(n)) return n;
    } catch {}
    return DEFAULT_PAGE_SIZE;
}

function persistSlidingPageSize(nextSize) {
    try {
        localStorage.setItem(SLIDING_PAGE_SIZE_STORAGE_KEY, String(nextSize));
    } catch {}
}

function getBrowseImageUrl(story) {
    if (!story) return null;
    const candidates = [
        story.imageUrl,
        story.image_url,
        story.imageIcon400Url,
        story.imageIcon200Url,
        story.imagePreviewUrl,
    ];
    for (const url of candidates) {
        if (typeof url === "string" && url.trim()) return url.trim();
    }
    return null;
}

function loadSlidingSortFilter() {
    try {
        const v = localStorage.getItem(SLIDING_SORT_STORAGE_KEY);
        if (VALID_SORT_VALUES.has(v)) return v;
    } catch {}
    return DEFAULT_SORT;
}

function loadSlidingTagsFilter() {
    try {
        const raw = localStorage.getItem(SLIDING_TAGS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
            .filter((tag) => tag.length > 0);
    } catch {}
    return [];
}

function loadSlidingKeywordFilter() {
    try {
        const v = localStorage.getItem(SLIDING_KEYWORD_STORAGE_KEY);
        return typeof v === "string" ? v : "";
    } catch {}
    return "";
}

function tagOptionLabel(option) {
    const name = typeof option === "string" ? option : (option?.tag ?? option?.name ?? String(option ?? ""));
    const count = typeof option === "object" && option != null ? option.count : undefined;
    if (count != null && count !== "") return `${name} (${count})`;
    return name;
}

function tagOptionValue(option) {
    return typeof option === "string" ? option : (option?.tag ?? option?.name ?? String(option ?? ""));
}

function sortTagOptions(tags) {
    if (!Array.isArray(tags)) return [];
    return [...tags].sort((a, b) =>
        tagOptionValue(a).localeCompare(tagOptionValue(b), undefined, { sensitivity: "base" }),
    );
}

const DEFAULT_GEN_SIZE = 10;
/** Difficulties expected per grid size (EASY, MEDIUM, HARD). */
const DIFFICULTIES_PER_GRID = DIFF_LEVELS.length;

function clampGenSize(value) {
    const n = parseInt(String(value ?? "").trim(), 10);
    if (!Number.isFinite(n)) return DEFAULT_GEN_SIZE;
    return Math.min(100, Math.max(1, n));
}

function getGroupStoryId(group) {
    if (!group || typeof group !== "object") return null;
    const sid = group.story?.id ?? group.storyId;
    return typeof sid === "string" && sid.trim() ? sid.trim() : null;
}

function getStoryTitle(story, preferredLanguage) {
    if (!story || typeof story !== "object") return "";
    const en = story.english?.title ?? story.en?.title;
    const fr = story.french?.title ?? story.fr?.title;
    const enStr = typeof en === "string" ? en.trim() : "";
    const frStr = typeof fr === "string" ? fr.trim() : "";
    const pref = typeof preferredLanguage === "string" ? preferredLanguage.trim().toLowerCase() : "";
    if (pref === "french") return frStr || enStr || "";
    if (pref === "english") return enStr || frStr || "";
    if (enStr) return enStr;
    if (frStr) return frStr;
    return "";
}

function formatLanguageLabel(language) {
    const s = typeof language === "string" ? language.trim() : "";
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function getSlidingGroupTags(group, story) {
    const fromPuzzleTemplate = group?.puzzleTemplate?.tags ?? group?.puzzle_template?.tags;
    if (Array.isArray(fromPuzzleTemplate) && fromPuzzleTemplate.length > 0) {
        return fromPuzzleTemplate
            .map((tag) => String(tag).trim())
            .filter((tag) => tag && tag.toLowerCase() !== "bedrock");
    }
    const fromStory = story?.tags;
    if (Array.isArray(fromStory) && fromStory.length > 0) {
        return fromStory
            .map((tag) => String(tag).trim())
            .filter((tag) => tag && tag.toLowerCase() !== "bedrock");
    }
    const summary = getStoryTemplateSummary(story);
    if (summary?.tags?.length) {
        return summary.tags.filter((tag) => tag.toLowerCase() !== "bedrock");
    }
    const genre = story?.topic?.genre?.name;
    if (typeof genre === "string" && genre.trim()) {
        const tag = genre.trim();
        return tag.toLowerCase() === "bedrock" ? [] : [tag];
    }
    return [];
}

function applyTagsToGroup(group, tags) {
    const nextTags = Array.isArray(tags) ? tags : [];
    const puzzleTemplate = group?.puzzleTemplate ?? group?.puzzle_template;
    return {
        ...group,
        story: group?.story ? { ...group.story, tags: nextTags } : group?.story,
        puzzleTemplate: { ...(puzzleTemplate || {}), tags: nextTags },
    };
}

function tagButtonLabel(displayTags) {
    if (!displayTags.length) return "Tag";
    if (displayTags.length === 1) return displayTags[0];
    return `${displayTags[0]} +${displayTags.length - 1}`;
}

/** Count template rows for a grid size (may include duplicates). */
function countByGridSize(templates, gridSize) {
    if (!Array.isArray(templates)) return 0;
    return templates.filter((t) => Number(t?.gridSize) === gridSize).length;
}

/** Count distinct difficulties (EASY/MEDIUM/HARD) present for a grid size. */
function countDifficultiesForGridSize(templates, gridSize) {
    if (!Array.isArray(templates)) return 0;
    return DIFF_LEVELS.filter((level) => templateForGridAndLevel(templates, gridSize, level)).length;
}

/**
 * Grid-size cell: green check when EASY + MEDIUM + HARD exist; otherwise difficulty fraction.
 * Raw template count can exceed 3 when duplicate rows exist — completeness is by difficulty.
 */
function GridSizeCell({ templates, gridSize }) {
    const diffCount = countDifficultiesForGridSize(templates, gridSize);
    const totalRows = countByGridSize(templates, gridSize);
    const complete = diffCount === DIFFICULTIES_PER_GRID;
    if (diffCount === 0) {
        return (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center" }}>
                —
            </Typography>
        );
    }
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            {complete ? (
                <Tooltip
                    title={
                        totalRows > DIFFICULTIES_PER_GRID
                            ? `${DIFFICULTIES_PER_GRID} difficulties (${totalRows} template rows)`
                            : `${DIFFICULTIES_PER_GRID} difficulties — complete`
                    }
                >
                    <CheckCircleIcon sx={{ color: "#3BA666", fontSize: 22 }} />
                </Tooltip>
            ) : (
                <Tooltip title={`${diffCount}/${DIFFICULTIES_PER_GRID} difficulties (${totalRows} template rows)`}>
                    <Chip
                        label={`${diffCount}/${DIFFICULTIES_PER_GRID}`}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 40, fontWeight: 600 }}
                    />
                </Tooltip>
            )}
        </Box>
    );
}

function templateHasPreviewImage(t) {
    const u = t?.previewImageUrl ?? t?.preview_image_url;
    return typeof u === "string" && u.trim() !== "";
}

/** Count templates with previewImageUrl set. */
function countWithPreviewImage(templates) {
    if (!Array.isArray(templates)) return 0;
    return templates.filter(templateHasPreviewImage).length;
}

/**
 * Preview column: green check when every template has previewImageUrl (same pattern as grid-size columns).
 */
function PreviewCell({ templates }) {
    const total = Array.isArray(templates) ? templates.length : 0;
    const count = countWithPreviewImage(templates);
    if (total === 0) {
        return (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center" }}>
                —
            </Typography>
        );
    }
    const complete = count === total;
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            {complete ? (
                <Tooltip title={`${count}/${total} templates — all have preview`}>
                    <CheckCircleIcon sx={{ color: "#3BA666", fontSize: 22 }} />
                </Tooltip>
            ) : (
                <Chip
                    label={`${count}/${total}`}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 40, fontWeight: 600 }}
                />
            )}
        </Box>
    );
}

/** State cell: shows storySliding.state as a chip. */
function StateCell({ storySliding }) {
    const state = storySliding?.state ?? null;
    if (!state) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    const isPublished = state === "PUBLISHED";
    return (
        <Chip
            size="small"
            label={state}
            color={isPublished ? "success" : "default"}
            variant={isPublished ? "filled" : "outlined"}
            sx={{ textTransform: "capitalize" }}
        />
    );
}

/** Published date cell. */
function PublishedAtCell({ storySliding }) {
    const publishedAt = storySliding?.publishedAt ?? storySliding?.published_at ?? null;
    if (!publishedAt) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    const d = new Date(publishedAt);
    if (!Number.isFinite(d.getTime())) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    return (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}{" "}
            {d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        </Typography>
    );
}

function SlidingBrowseStoryRow({
    group,
    index,
    navigate,
    savingFreeStoryId,
    onStoryFreeChange,
    languageFilter,
    publishingStoryId,
    onPublishStory,
    markingReviewedStoryId,
    onMarkReviewed,
    onEditTags,
    onZoomImage,
}) {
    const templates = group?.templates;
    const story = group?.story;
    const storySliding = group?.storySliding ?? null;
    const storyId = getGroupStoryId(group);
    const displayTags = getSlidingGroupTags(group, story);
    const languageLabel = formatLanguageLabel(story?.language);
    const bundleState = storySliding?.state ?? "";
    const standalone = Boolean(story?.standaloneImage);
    const canPublish =
        Array.isArray(templates) &&
        templates.length > 0 &&
        (bundleState === "REVIEWED" ||
            (standalone && (bundleState === "DRAFT" || bundleState === "REVIEWED")));
    const canMarkReviewed =
        bundleState === "DRAFT" && Array.isArray(templates) && templates.length > 0 && !standalone;
    const isPublishing = Boolean(storyId && publishingStoryId === storyId);
    const isMarkingReviewed = Boolean(storyId && markingReviewedStoryId === storyId);
    const imageUrl =
        typeof story?.imageUrl === "string" && story.imageUrl.trim()
            ? story.imageUrl.trim()
            : typeof story?.image_url === "string" && story.image_url.trim()
              ? story.image_url.trim()
              : null;
    const freeCb = getStoryFreeCheckboxState(group);
    const savingFree = Boolean(storyId && savingFreeStoryId === storyId);
    const canToggleFree = Boolean(storyId && Array.isArray(templates) && templates.length > 0);

    const handleFreeCheckboxChange = (event, nextChecked) => {
        event.stopPropagation();
        if (!storyId || !canToggleFree || savingFree) return;
        onStoryFreeChange(storyId, nextChecked);
    };

    return (
        <TableRow
            key={storyId || `sliding-group-${index}`}
            hover
            onClick={() => storyId && navigate(`/admin/sliding/${encodeURIComponent(storyId)}`)}
            sx={{ cursor: storyId ? "pointer" : "default" }}
        >
            {/* Story column */}
            <TableCell sx={{ verticalAlign: "middle" }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", minWidth: 0 }}>
                    {imageUrl ? (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            sx={{
                                width: 112,
                                height: 112,
                                objectFit: "cover",
                                borderRadius: 1,
                                flexShrink: 0,
                                border: 1,
                                borderColor: "divider",
                            }}
                        />
                    ) : null}
                    <Box sx={{ minWidth: 0 }}>
                        {storyId ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                                {getStoryTitle(story, languageFilter) || storyId}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                        {(languageLabel || displayTags.length > 0) ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                {languageLabel ? (
                                    <Chip size="small" variant="outlined" label={languageLabel} />
                                ) : null}
                                {displayTags.map((tag) => (
                                    <Chip key={tag} size="small" variant="outlined" label={tag} />
                                ))}
                                {standalone ? (
                                    <Chip size="small" color="info" variant="outlined" label="Standalone image" />
                                ) : null}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </TableCell>

            {/* State column */}
            <TableCell sx={{ verticalAlign: "middle" }}>
                <StateCell storySliding={storySliding} />
            </TableCell>

            {/* Free column */}
            <TableCell
                sx={{ verticalAlign: "middle" }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox
                        size="small"
                        checked={freeCb.checked}
                        indeterminate={freeCb.indeterminate}
                        disabled={!canToggleFree || savingFree}
                        onChange={handleFreeCheckboxChange}
                        inputProps={{ "aria-label": freeCb.checked ? "Free, click to set paid" : "Paid, click to set free" }}
                    />
                    <Tooltip title="Zoom image">
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Zoom ${getStoryTitle(story, languageFilter) || storyId || "image"}`}
                                disabled={!imageUrl}
                                onClick={() => {
                                    if (!imageUrl) return;
                                    onZoomImage?.({
                                        url: getBrowseImageUrl(story) || imageUrl,
                                        title: getStoryTitle(story, languageFilter) || storyId || "Sliding",
                                        storyId,
                                        state: bundleState,
                                        free: freeCb.checked,
                                        standalone,
                                        templates,
                                    });
                                }}
                                sx={{ width: 44, height: 44, flexShrink: 0 }}
                            >
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </TableCell>

            {/* Published column */}
            <TableCell sx={{ verticalAlign: "middle" }}>
                <PublishedAtCell storySliding={storySliding} />
            </TableCell>

            {/* 4×4 column */}
            <TableCell sx={{ verticalAlign: "middle", textAlign: "center" }}>
                <GridSizeCell templates={templates} gridSize={4} />
            </TableCell>

            {/* 6×6 column */}
            <TableCell sx={{ verticalAlign: "middle", textAlign: "center" }}>
                <GridSizeCell templates={templates} gridSize={6} />
            </TableCell>

            {/* Tag column */}
            <TableCell
                sx={{ verticalAlign: "middle" }}
                onClick={(e) => e.stopPropagation()}
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LocalOfferOutlinedIcon />}
                    disabled={!storyId}
                    onClick={() => onEditTags?.(group)}
                    sx={{
                        textTransform: "none",
                        minHeight: 44,
                        maxWidth: 140,
                        justifyContent: "flex-start",
                    }}
                >
                    <Typography component="span" variant="body2" noWrap>
                        {tagButtonLabel(displayTags)}
                    </Typography>
                </Button>
            </TableCell>

            {/* Preview column */}
            <TableCell sx={{ verticalAlign: "middle", textAlign: "center" }}>
                <PreviewCell templates={templates} />
            </TableCell>

            {/* Action column */}
            <TableCell
                align="center"
                sx={{ verticalAlign: "middle" }}
                onClick={(e) => e.stopPropagation()}
            >
                {canMarkReviewed ? (
                    <Tooltip title="Mark REVIEWED">
                        <span>
                            <IconButton
                                size="small"
                                color="secondary"
                                aria-label="Mark sliding story reviewed"
                                disabled={isMarkingReviewed}
                                onClick={() => onMarkReviewed(storyId)}
                            >
                                {isMarkingReviewed ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <RateReviewIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : canPublish ? (
                    <Tooltip title="Publish">
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                aria-label="Publish sliding story"
                                disabled={isPublishing}
                                onClick={() => onPublishStory(storyId, templates)}
                            >
                                {isPublishing ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <PublishIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : bundleState === "PUBLISHED" ? (
                    <Tooltip title="Already published">
                        <CheckCircleIcon fontSize="small" color="success" />
                    </Tooltip>
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>
        </TableRow>
    );
}

export default function AdminSlidingMain() {
    const navigate = useNavigate();

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(loadSlidingPageSize);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPagesFromApi, setTotalPagesFromApi] = useState(undefined);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [genDialogOpen, setGenDialogOpen] = useState(false);
    const [genSizeInput, setGenSizeInput] = useState(String(DEFAULT_GEN_SIZE));
    const [genMakeWeakAgo, setGenMakeWeakAgo] = useState(false);
    const [genSubmitting, setGenSubmitting] = useState(false);
    const [genResult, setGenResult] = useState(null);
    const [preImageDialogOpen, setPreImageDialogOpen] = useState(false);
    const [preImageSizeInput, setPreImageSizeInput] = useState(String(DEFAULT_GEN_SIZE));
    const [preImageSubmitting, setPreImageSubmitting] = useState(false);
    const [preImageResult, setPreImageResult] = useState(null);
    const [preImageRefreshDialogOpen, setPreImageRefreshDialogOpen] = useState(false);
    const [savingFreeStoryId, setSavingFreeStoryId] = useState(null);
    const [publishingStoryId, setPublishingStoryId] = useState(null);
    const [markingReviewedStoryId, setMarkingReviewedStoryId] = useState(null);
    const [stateFilter, setStateFilter] = useState(loadSlidingStateFilter);
    const [languageFilter, setLanguageFilter] = useState(loadSlidingLanguageFilter);
    const [keyword, setKeyword] = useState(loadSlidingKeywordFilter);
    const [selectedTags, setSelectedTags] = useState(loadSlidingTagsFilter);
    const [freeFilter, setFreeFilter] = useState(loadSlidingFreeFilter);
    const [standaloneFilter, setStandaloneFilter] = useState(loadSlidingStandaloneFilter);
    const [viewMode, setViewMode] = useState(loadSlidingViewMode);
    const [browseTag, setBrowseTag] = useState(loadSlidingBrowseTag);
    const [sort, setSort] = useState(loadSlidingSortFilter);
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagDialog, setTagDialog] = useState(null);
    const [tagDraft, setTagDraft] = useState([]);
    const [savingTags, setSavingTags] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [disabling, setDisabling] = useState(false);

    const sortedAvailableTags = useMemo(
        () => sortTagOptions(availableTags),
        [availableTags],
    );

    const browseTagCount = useMemo(() => {
        if (viewMode !== "tags" || !browseTag) return null;
        const row = sortedAvailableTags.find(
            (option) => tagOptionValue(option).toLowerCase() === browseTag.toLowerCase(),
        );
        return typeof row?.count === "number" && row.count >= 0 ? row.count : null;
    }, [viewMode, browseTag, sortedAvailableTags]);

    const fetchList = useCallback(async () => {
        if (viewMode === "tags" && !browseTag) {
            setGroups([]);
            setTotalElements(0);
            setTotalPagesFromApi(1);
            setLoading(false);
            return;
        }
        setLoading(true);
        const pageIndex = Math.max(0, page - 1);
        const freeParam =
            freeFilter === "true" ? true : freeFilter === "false" ? false : undefined;
        const standaloneParam =
            standaloneFilter === "true" ? true : standaloneFilter === "false" ? false : undefined;
        const tagsForApi = viewMode === "tags" ? [browseTag] : selectedTags;
        const keywordForApi = viewMode === "tags" ? "" : keyword;
        const result = await AdminSlidingService.list(
            pageIndex,
            pageSize,
            sort,
            stateFilter || undefined,
            keywordForApi,
            languageFilter || undefined,
            tagsForApi,
            freeParam,
            standaloneParam,
        );
        setLoading(false);
        if (result.success && result.data) {
            const visibleGroups = (Array.isArray(result.data.content) ? result.data.content : []).filter((group) => {
                if (freeFilter !== "true" && freeFilter !== "false") return true;
                const isFree = group?.storySliding?.free === true;
                return freeFilter === "true" ? isFree : !isFree;
            });
            setGroups(visibleGroups);
            setTotalElements(result.data.totalElements);
            let nextTotalPages = result.data.totalPages;
            if (viewMode === "tags" && browseTagCount != null) {
                nextTotalPages = Math.max(1, Math.ceil(browseTagCount / pageSize));
            }
            setTotalPagesFromApi(nextTotalPages);
            if (typeof nextTotalPages === "number" && nextTotalPages >= 1 && page > nextTotalPages) {
                setPage(nextTotalPages);
                return;
            }
        } else {
            setGroups([]);
            setTotalElements(0);
            setTotalPagesFromApi(undefined);
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load sliding puzzles" });
        }
    }, [page, pageSize, stateFilter, languageFilter, keyword, selectedTags, freeFilter, standaloneFilter, sort, viewMode, browseTag, browseTagCount]);

    const handleKeywordChange = useCallback((kw) => {
        setKeyword(kw);
        setPage(1);
        try {
            localStorage.setItem(SLIDING_KEYWORD_STORAGE_KEY, typeof kw === "string" ? kw : "");
        } catch {}
    }, []);

    const handleTagsChange = useCallback((newTags) => {
        setSelectedTags(newTags);
        setPage(1);
        try {
            localStorage.setItem(SLIDING_TAGS_STORAGE_KEY, JSON.stringify(newTags));
        } catch {}
    }, []);

    const handlePageSizeChange = (e) => {
        const next = Number(e.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        setPageSize(next);
        persistSlidingPageSize(next);
        setPage(1);
    };

    const handleViewModeChange = useCallback((_event, next) => {
        if (!next) return;
        setViewMode(next);
        setPage(1);
        try { localStorage.setItem(SLIDING_VIEW_STORAGE_KEY, next); } catch {}
    }, []);

    const handleBrowseTagClick = useCallback((tag) => {
        const normalized = String(tag ?? "").trim();
        if (!normalized) return;
        setTotalPagesFromApi(1);
        setPage(1);
        setBrowseTag((prev) => {
            const next = prev && prev.toLowerCase() === normalized.toLowerCase() ? "" : normalized;
            try { localStorage.setItem(SLIDING_BROWSE_TAG_STORAGE_KEY, next); } catch {}
            return next;
        });
    }, []);

    const showImageGrid = viewMode === "image" || viewMode === "tags";

    useEffect(() => {
        let cancelled = false;
        const fetchTags = async () => {
            setTagsLoading(true);
            const result = await AdminSlidingService.getDistinctTags(
                stateFilter || undefined,
                languageFilter || undefined,
            );
            if (cancelled) return;
            setTagsLoading(false);
            if (result.success) {
                setAvailableTags(result.data ?? []);
            } else {
                setAvailableTags([]);
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: result.error || "Could not load tag suggestions",
                });
            }
        };
        fetchTags();
        return () => {
            cancelled = true;
        };
    }, [stateFilter, languageFilter]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleOpenGenDialog = useCallback(() => {
        setGenSizeInput(String(DEFAULT_GEN_SIZE));
        setGenMakeWeakAgo(false);
        setGenDialogOpen(true);
    }, []);

    const handleGenSubmit = useCallback(async () => {
        const size = clampGenSize(genSizeInput);
        setGenSizeInput(String(size));
        setGenSubmitting(true);
        const result = await AdminSlidingService.genSlidingNullStory(size, genMakeWeakAgo);
        setGenSubmitting(false);
        setGenDialogOpen(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Generation failed" });
            return;
        }
        setGenResult(result.data);
    }, [genSizeInput, genMakeWeakAgo]);

    const handleCloseGenResult = useCallback(() => {
        setGenResult(null);
        fetchList();
    }, [fetchList]);

    const handleOpenPreImageDialog = useCallback(() => {
        setPreImageSizeInput(String(DEFAULT_GEN_SIZE));
        setPreImageResult(null);
        setPreImageDialogOpen(true);
    }, []);

    const handlePreImageConfirm = useCallback(async () => {
        const size = clampGenSize(preImageSizeInput);
        setPreImageSizeInput(String(size));
        setPreImageSubmitting(true);
        setPreImageResult(null);
        const result = await AdminSlidingService.genPreImageForNull(size);
        setPreImageSubmitting(false);
        if (!result.success) {
            setPreImageResult({ error: result.error || "Failed to generate preview images" });
            return;
        }
        setPreImageResult(result.data);
    }, [preImageSizeInput]);

    const handleClosePreImageDialog = useCallback(() => {
        setPreImageDialogOpen(false);
        setPreImageResult(null);
        fetchList();
    }, [fetchList]);

    const handleOpenPreImageRefreshDialog = useCallback(() => {
        setPreImageRefreshDialogOpen(true);
    }, []);

    const handleClosePreImageRefreshDialog = useCallback(() => {
        setPreImageRefreshDialogOpen(false);
    }, []);

    const startPreImageRefreshJob = useCallback(
        (size, opts) => AdminSlidingService.genPreImageRefreshAsync(size, opts),
        []
    );

    const fetchPreImageRefreshJob = useCallback(
        (jobId) => AdminSlidingService.getPreImageRefreshJob(jobId),
        []
    );

    const cancelPreImageRefreshJob = useCallback(
        (jobId) => AdminSlidingService.cancelPreImageRefreshJob(jobId),
        []
    );

    const handleStoryFreeChange = useCallback(async (storyId, free) => {
        setSavingFreeStoryId(storyId);
        const result = await AdminSlidingService.setStoryTemplatesFree(storyId, free);
        setSavingFreeStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update free flag" });
            return;
        }
        setGroups((prev) => prev.flatMap((group) => {
            if (getGroupStoryId(group) !== storyId) return [group];
            const next = {
                ...group,
                storySliding: group.storySliding ? { ...group.storySliding, free } : group.storySliding,
            };
            if (freeFilter === "true" && !free) return [];
            if (freeFilter === "false" && free) return [];
            return [next];
        }));
        setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, free } : prev));
        await fetchList();
    }, [fetchList, freeFilter]);

    const openTagDialog = useCallback((group) => {
        const story = group?.story;
        const storyId = getGroupStoryId(group);
        if (!storyId) return;
        setTagDialog({
            storyId,
            title: getStoryTitle(story, languageFilter) || storyId,
        });
        setTagDraft(getSlidingGroupTags(group, story));
    }, [languageFilter]);

    const handleCloseTagDialog = useCallback(() => {
        if (savingTags) return;
        setTagDialog(null);
        setTagDraft([]);
    }, [savingTags]);

    const handleSaveTags = useCallback(async () => {
        const storyId = tagDialog?.storyId;
        if (!storyId || savingTags) return;
        const nextTags = tagDraft
            .map((tag) => tagOptionValue(tag).trim())
            .filter((tag) => tag.length > 0);
        setSavingTags(true);
        const result = await AdminSlidingService.setStoryTags(storyId, nextTags);
        setSavingTags(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update tags" });
            return;
        }
        setGroups((prev) => prev.map((g) => (getGroupStoryId(g) === storyId ? applyTagsToGroup(g, nextTags) : g)));
        setTagDialog(null);
        setTagDraft([]);
        setSnackbar({ open: true, severity: "success", message: "Tags updated" });
        fetchList();
    }, [tagDialog, tagDraft, savingTags, fetchList]);

    const handlePickTagFromList = useCallback((tagName) => {
        const name = String(tagName || "").trim();
        if (!name) return;
        setTagDraft((prev) => {
            const has = prev.some((t) => tagOptionValue(t).toLowerCase() === name.toLowerCase());
            if (has) return prev.filter((t) => tagOptionValue(t).toLowerCase() !== name.toLowerCase());
            return [name];
        });
    }, []);

    const handleConfirmRegenImage = useCallback(async (storyId) => {
        if (!storyId || regenLoading) return;
        setRegenLoading(true);
        try {
            const result = await AdminStoryService.regenImage(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Regenerate image failed",
                });
                return;
            }
            const nextUrl = getBrowseImageUrl(result.data) || zoomImage?.url || "";
            const busted = nextUrl
                ? `${nextUrl}${nextUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
                : "";
            if (busted) {
                setZoomImage((prev) => (prev ? { ...prev, url: busted } : prev));
            }
            const preview = await AdminSlidingService.genPreview(storyId);
            setSnackbar({
                open: true,
                severity: preview.success ? "success" : "warning",
                message: preview.success
                    ? "Image replaced. Sliding puzzle images were regenerated."
                    : "Image replaced. Sliding preview refresh failed — use Gen preview if needed.",
            });
            await fetchList();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Regenerate image failed",
            });
        } finally {
            setRegenLoading(false);
        }
    }, [fetchList, regenLoading, zoomImage?.url]);

    const handleConfirmDelete = useCallback(async (storyId) => {
        if (!storyId || deleting) return;
        setDeleting(true);
        try {
            const result = await AdminSlidingService.removeByStoryId(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            setZoomImage(null);
            setSnackbar({ open: true, severity: "success", message: "Sliding puzzle deleted" });
            await fetchList();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Delete failed",
            });
        } finally {
            setDeleting(false);
        }
    }, [deleting, fetchList]);

    const handleConfirmDisable = useCallback(async (storyId) => {
        if (!storyId || disabling) return;
        setDisabling(true);
        try {
            const result = await AdminSlidingService.disableStory(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Disable failed",
                });
                return;
            }
            setZoomImage((prev) => (prev ? { ...prev, state: "DISABLED" } : prev));
            setSnackbar({
                open: true,
                severity: "success",
                message: "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.",
            });
            await fetchList();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Disable failed",
            });
        } finally {
            setDisabling(false);
        }
    }, [disabling, fetchList]);

    const handlePublishStory = useCallback(async (storyId, templates) => {
        if (!storyId) return;
        if (!Array.isArray(templates) || templates.length === 0) {
            setSnackbar({ open: true, severity: "error", message: "No sliding templates to publish" });
            return;
        }
        setPublishingStoryId(storyId);
        const result = await AdminSlidingService.publishStory(storyId);
        setPublishingStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Publish failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Published successfully" });
        setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, state: "PUBLISHED" } : prev));
        fetchList();
    }, [fetchList]);

    const handleMarkReviewed = useCallback(async (storyId) => {
        if (!storyId) return;
        setMarkingReviewedStoryId(storyId);
        const result = await AdminSlidingService.markReviewed(storyId);
        setMarkingReviewedStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Mark reviewed failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Marked REVIEWED" });
        fetchList();
    }, [fetchList]);

    const totalPages =
        typeof totalPagesFromApi === "number" && totalPagesFromApi >= 1
            ? totalPagesFromApi
            : Math.max(1, Math.ceil(totalElements / pageSize));

    const COL_SPAN = 9;

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1, width: "100%" }}>
                    <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="flex-end"
                    >
                        <Button variant="outlined" color="primary" onClick={() => setPublishDialogOpen(true)}>
                            Publish
                        </Button>
                        <Button variant="outlined" color="primary" onClick={handleOpenPreImageDialog}>
                            Generate preview images
                        </Button>
                        <Button variant="outlined" color="primary" onClick={handleOpenPreImageRefreshDialog}>
                            Gen preview image(refresh)
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleOpenGenDialog}>
                            Generate sliding puzzles
                        </Button>
                    </Stack>
                </Paper>
                <Typography variant="h5">Sliding puzzles</Typography>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewMode}
                        onChange={handleViewModeChange}
                        aria-label="Browse view"
                    >
                        <ToggleButton value="list" aria-label="List view" title="List" sx={{ px: 1 }}>
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="image" aria-label="Image view" title="Image" sx={{ px: 1 }}>
                            <ViewModuleIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="tags" aria-label="Browse by tag" title="Browse by tag" sx={{ px: 1 }}>
                            <LocalOfferOutlinedIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={stateFilter}
                            label="State"
                            onChange={(e) => {
                                setStateFilter(e.target.value);
                                setPage(1);
                                try { localStorage.setItem(SLIDING_STATE_STORAGE_KEY, e.target.value); } catch {}
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="PUBLISHED">Published</MenuItem>
                            <MenuItem value="REVIEWED">Reviewed</MenuItem>
                            <MenuItem value="DRAFT">Draft</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="sliding-order-by-label">Order by</InputLabel>
                        <Select
                            labelId="sliding-order-by-label"
                            value={sort}
                            label="Order by"
                            onChange={(e) => {
                                const next = e.target.value;
                                setSort(next);
                                setPage(1);
                                try { localStorage.setItem(SLIDING_SORT_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {ORDER_BY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="sliding-free-filter-label">Access</InputLabel>
                        <Select
                            labelId="sliding-free-filter-label"
                            value={freeFilter}
                            label="Access"
                            onChange={(e) => {
                                const next = e.target.value;
                                setFreeFilter(next);
                                setPage(1);
                                try { localStorage.setItem(SLIDING_FREE_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {FREE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="sliding-standalone-filter-label">Standalone</InputLabel>
                        <Select
                            labelId="sliding-standalone-filter-label"
                            value={standaloneFilter}
                            label="Standalone"
                            onChange={(e) => {
                                const next = e.target.value;
                                setStandaloneFilter(next);
                                setPage(1);
                                try { localStorage.setItem(SLIDING_STANDALONE_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {STANDALONE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={languageFilter}
                            label="Language"
                            onChange={(e) => {
                                setLanguageFilter(e.target.value);
                                setPage(1);
                                try { localStorage.setItem(SLIDING_LANGUAGE_STORAGE_KEY, e.target.value); } catch {}
                            }}
                        >
                            {LANGUAGE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {viewMode !== "tags" ? (
                        <>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        disableCloseOnSelect
                        options={availableTags}
                        value={selectedTags.map(
                            (s) => availableTags.find((o) => tagOptionValue(o).toLowerCase() === s.toLowerCase()) ?? s,
                        )}
                        isOptionEqualToValue={(opt, val) =>
                            tagOptionValue(opt).toLowerCase() === tagOptionValue(val).toLowerCase()
                        }
                        getOptionKey={(option) => tagOptionValue(option).toLowerCase()}
                        filterOptions={(options, params) =>
                            filterAdminTagOptions(options, params, tagOptionValue, selectedTags)
                        }
                        onChange={(_event, newValue) => {
                            handleTagsChange(
                                newValue.map((tag) => tagOptionValue(tag).trim()).filter((tag) => tag.length > 0),
                            );
                        }}
                        loading={tagsLoading}
                        noOptionsText={tagsLoading ? "Loading tags…" : "Type a tag and press Enter"}
                        slotProps={adminTagFilterAutocompleteSlotProps}
                        sx={{
                            minWidth: 200,
                            maxWidth: 280,
                            width: { xs: "100%", sm: 240 },
                        }}
                        getOptionLabel={tagOptionLabel}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={tagOptionValue(option)}
                                    {...getTagProps({ index })}
                                    sx={adminTagFilterChipSx}
                                    key={`${tagOptionValue(option)}-${index}`}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Tags" placeholder="Type or pick a tag" />
                        )}
                    />
                    <KeywordBox
                        callback={handleKeywordChange}
                        initialValue={keyword}
                        label="Search"
                        placeholder="Search by title"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 220 }}
                    />
                        </>
                    ) : null}
                </Stack>
                {viewMode === "tags" ? (
                    <Paper variant="outlined" sx={{ p: 1.25, width: "100%" }}>
                        {tagsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : sortedAvailableTags.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No tags for the current filters.
                            </Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                {sortedAvailableTags.map((option) => {
                                    const tag = tagOptionValue(option);
                                    const selected = browseTag
                                        && browseTag.toLowerCase() === tag.toLowerCase();
                                    return (
                                        <Chip
                                            key={tag.toLowerCase()}
                                            label={tagOptionLabel(option)}
                                            onClick={() => handleBrowseTagClick(tag)}
                                            color={selected ? "primary" : "default"}
                                            variant={selected ? "filled" : "outlined"}
                                            sx={{ ...adminTagFilterChipSx, cursor: "pointer" }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>
                ) : null}
            </Stack>

            <BatchPublishReviewedDialog
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
                title="Publish reviewed sliding puzzles"
                publishFn={(batchSize, language) => AdminSlidingService.publishReviewed(batchSize, language)}
                onComplete={fetchList}
            />

            {/* Generate dialog */}
            <Dialog open={genDialogOpen} onClose={() => { if (!genSubmitting) setGenDialogOpen(false); }} maxWidth="xs" fullWidth>
                <DialogTitle>Generate sliding puzzles</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Batch size"
                        type="number"
                        fullWidth
                        value={genSizeInput}
                        onChange={(e) => setGenSizeInput(e.target.value)}
                        disabled={genSubmitting}
                        slotProps={{ htmlInput: { min: 1, max: 100 } }}
                        helperText="Maximum stories to process (1–100). Default 10."
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={genMakeWeakAgo}
                                onChange={(_, checked) => setGenMakeWeakAgo(checked)}
                                disabled={genSubmitting}
                            />
                        }
                        label="Make weak ago"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenDialogOpen(false)} disabled={genSubmitting}>Cancel</Button>
                    <Button onClick={handleGenSubmit} variant="contained" disabled={genSubmitting}>
                        {genSubmitting ? <CircularProgress size={22} /> : "Generate"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generate preview images dialog */}
            <Dialog
                open={preImageDialogOpen}
                onClose={() => {
                    if (!preImageSubmitting) handleClosePreImageDialog();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Generate preview images</DialogTitle>
                <DialogContent>
                    {preImageSubmitting ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">
                                Generating preview images for sliding puzzles…
                            </Typography>
                        </Box>
                    ) : preImageResult ? (
                        preImageResult.error ? (
                            <Typography color="error">{preImageResult.error}</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                <Typography variant="body2">
                                    <strong>Total found:</strong> {preImageResult.totalFound ?? "—"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Processed:</strong> {preImageResult.processed ?? "—"}
                                </Typography>
                                {Array.isArray(preImageResult.titles) && preImageResult.titles.length > 0 ? (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Processed
                                        </Typography>
                                        <Box
                                            sx={{
                                                maxHeight: 200,
                                                overflow: "auto",
                                                p: 1,
                                                bgcolor: "grey.100",
                                                borderRadius: 1,
                                            }}
                                        >
                                            {preImageResult.titles.map((item, idx) => (
                                                <Typography
                                                    key={idx}
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ wordBreak: "break-word", mb: 0.5 }}
                                                >
                                                    <strong>{item?.title ?? item?.storyTitle ?? "—"}</strong>
                                                    {item?.tplId || item?.slidingId
                                                        ? ` (${item.tplId ?? item.slidingId})`
                                                        : ""}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : null}
                                {Array.isArray(preImageResult.errors) && preImageResult.errors.length > 0 ? (
                                    <Box>
                                        <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                                            Errors
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                            {preImageResult.errors.map((err, idx) => (
                                                <Typography component="li" variant="body2" color="error" key={`pre-img-err-${idx}`}>
                                                    {String(err)}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : null}
                            </Stack>
                        )
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Batch size"
                                type="number"
                                fullWidth
                                value={preImageSizeInput}
                                onChange={(e) => setPreImageSizeInput(e.target.value)}
                                disabled={preImageSubmitting}
                                slotProps={{ htmlInput: { min: 1, max: 100 } }}
                                helperText="Maximum stories to process (1–100). Default 10."
                                sx={{ mt: 1 }}
                            />
                            <Typography sx={{ mt: 2 }}>
                                Generate preview images for sliding templates that are missing preview assets?
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {preImageResult && !preImageSubmitting ? (
                        <Button onClick={handleClosePreImageDialog} variant="contained">
                            Close
                        </Button>
                    ) : !preImageSubmitting ? (
                        <>
                            <Button onClick={() => setPreImageDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handlePreImageConfirm} variant="contained" color="primary">
                                Generate
                            </Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <PreviewRefreshJobDialog
                open={preImageRefreshDialogOpen}
                onClose={handleClosePreImageRefreshDialog}
                title="Gen preview image(refresh)"
                description={
                    <>
                        Regenerates share preview and board PNGs for <strong>REVIEWED</strong> and{" "}
                        <strong>PUBLISHED</strong> stories that already have assets, overwriting the S3 files
                        with the current renderer policy. Rendering runs on the server, so progress is polled
                        here instead of blocking the request.
                    </>
                }
                maxSize={100}
                startJobFn={startPreImageRefreshJob}
                fetchJobFn={fetchPreImageRefreshJob}
                cancelJobFn={cancelPreImageRefreshJob}
                onComplete={fetchList}
            />

            {/* Generation result dialog */}
            <Dialog open={Boolean(genResult)} onClose={handleCloseGenResult} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>Generation result</DialogTitle>
                <DialogContent dividers>
                    {genResult ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2"><strong>Candidates:</strong> {genResult.candidates ?? "—"}</Typography>
                            <Typography variant="body2"><strong>Processed:</strong> {genResult.processed ?? "—"}</Typography>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Results</Typography>
                                {Array.isArray(genResult.results) && genResult.results.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {genResult.results.map((row, idx) => (
                                            <Typography component="li" variant="body2" key={`${row?.storyId}-${idx}`}>
                                                {row?.storyId ?? "—"}{row?.templatesCreated != null ? ` — templates created: ${row.templatesCreated}` : ""}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No result rows.</Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Errors</Typography>
                                {Array.isArray(genResult.errors) && genResult.errors.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {genResult.errors.map((err, idx) => (
                                            <Typography component="li" variant="body2" color="error" key={`err-${idx}`}>
                                                {String(err)}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGenResult} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(tagDialog)} onClose={handleCloseTagDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Update tag</DialogTitle>
                <DialogContent dividers>
                    {tagDialog?.title ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tagDialog.title}
                        </Typography>
                    ) : null}
                    {tagsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : sortedAvailableTags.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No tags available.
                        </Typography>
                    ) : (
                        <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
                            {sortedAvailableTags.map((option) => {
                                const name = tagOptionValue(option);
                                if (!name) return null;
                                const selected = tagDraft.some(
                                    (t) => tagOptionValue(t).toLowerCase() === name.toLowerCase(),
                                );
                                return (
                                    <ListItemButton
                                        key={name}
                                        selected={selected}
                                        disabled={savingTags}
                                        onClick={() => handlePickTagFromList(name)}
                                        sx={{ minHeight: 44 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {selected ? <CheckIcon fontSize="small" color="success" /> : null}
                                        </ListItemIcon>
                                        <ListItemText primary={tagOptionLabel(option)} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTagDialog} disabled={savingTags}>Cancel</Button>
                    <Button onClick={handleSaveTags} variant="contained" disabled={savingTags}>
                        {savingTags ? <CircularProgress size={22} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {showImageGrid ? (
                <Box>
                    {viewMode === "tags" && !browseTag ? (
                        <Paper sx={{ py: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">Select a tag above to browse puzzles.</Typography>
                        </Paper>
                    ) : loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : groups.length === 0 ? (
                        <Paper sx={{ py: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No sliding puzzle groups found.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={1.5}>
                            {groups.map((group, idx) => {
                                const story = group?.story;
                                const storyId = getGroupStoryId(group);
                                const imageUrl = getBrowseImageUrl(story);
                                const title = getStoryTitle(story, languageFilter) || storyId || "Sliding";
                                const displayTags = getSlidingGroupTags(group, story);
                                const bundleState = group?.storySliding?.state ?? "";
                                const isFree = getStoryFreeCheckboxState(group).checked;
                                return (
                                    <Grid key={storyId || idx} size={{ xs: 6, sm: 4, md: 3 }}>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                title={title}
                                                sx={{ mb: 0.5, fontWeight: 600, lineHeight: 1.3 }}
                                            >
                                                {title}
                                            </Typography>
                                            <Box
                                            onClick={() => storyId && navigate(`/admin/sliding/${encodeURIComponent(storyId)}`)}
                                            sx={{
                                                position: "relative",
                                                width: "100%",
                                                aspectRatio: "1 / 1",
                                                borderRadius: 1,
                                                overflow: "hidden",
                                                border: 1,
                                                borderColor: "divider",
                                                bgcolor: "grey.900",
                                                cursor: storyId ? "pointer" : "default",
                                            }}
                                        >
                                            {imageUrl ? (
                                                <Box
                                                    component="img"
                                                    src={imageUrl}
                                                    alt={title}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <Typography variant="caption" color="grey.500">
                                                        No image
                                                    </Typography>
                                                </Box>
                                            )}
                                            {isFree ? (
                                            <Chip
                                                size="small"
                                                label="Free"
                                                color="success"
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    zIndex: 2,
                                                    fontWeight: 700,
                                                    pointerEvents: "none",
                                                }}
                                            />
                                            ) : null}
                                            </Box>
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LocalOfferOutlinedIcon />}
                                                disabled={!storyId}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openTagDialog(group);
                                                }}
                                                sx={{
                                                    minHeight: 44,
                                                    textTransform: "none",
                                                    justifyContent: "flex-start",
                                                    px: 1,
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                                                >
                                                    {tagButtonLabel(displayTags)}
                                                </Typography>
                                            </Button>
                                            <Tooltip title="Zoom image">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Zoom ${title}`}
                                                        disabled={!imageUrl}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!imageUrl) return;
                                                            setZoomImage({
                                                                url: imageUrl,
                                                                title,
                                                                storyId,
                                                                state: bundleState,
                                                                free: isFree,
                                                                standalone: Boolean(story?.standaloneImage),
                                                                templates: group?.templates,
                                                            });
                                                        }}
                                                        sx={{ width: 44, height: 44, flexShrink: 0 }}
                                                    >
                                                        <ZoomInIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            ) : (
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer sx={{ maxWidth: "100%" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 300 }}>Story</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>State</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>Free</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Published</TableCell>
                                <TableCell sx={{ minWidth: 80, textAlign: "center" }}>4×4</TableCell>
                                <TableCell sx={{ minWidth: 80, textAlign: "center" }}>6×6</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Tag</TableCell>
                                <TableCell sx={{ minWidth: 80, textAlign: "center" }}>Preview</TableCell>
                                <TableCell sx={{ minWidth: 72, width: 72 }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} />
                                    </TableCell>
                                </TableRow>
                            ) : groups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 4 }}>
                                        No sliding puzzle groups found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groups.map((group, index) => (
                                    <SlidingBrowseStoryRow
                                        key={getGroupStoryId(group) || `sliding-group-${index}`}
                                        group={group}
                                        index={index}
                                        navigate={navigate}
                                        savingFreeStoryId={savingFreeStoryId}
                                        onStoryFreeChange={handleStoryFreeChange}
                                        languageFilter={languageFilter}
                                        publishingStoryId={publishingStoryId}
                                        onPublishStory={handlePublishStory}
                                        markingReviewedStoryId={markingReviewedStoryId}
                                        onMarkReviewed={handleMarkReviewed}
                                        onEditTags={openTagDialog}
                                        onZoomImage={setZoomImage}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            )}

            {(viewMode !== "tags" || Boolean(browseTag)) && (
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ mt: 2 }}
            >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="sliding-browse-page-size-label">Page size</InputLabel>
                    <Select
                        labelId="sliding-browse-page-size-label"
                        label="Page size"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Pagination
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Stack>
            )}

            <ConfirmDialog
                open={regenDialogOpen}
                setOpen={setRegenDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration and regenerates sliding puzzle images. Story text is not changed."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This sliding puzzle"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete sliding puzzle?"
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This sliding puzzle"
                }
                targetId={zoomImage?.storyId}
                message={[
                    "This permanently deletes all sliding templates, preview images, and play history for this story.",
                    "Sliding is discarded so scheduled jobs will not recreate it. The story record stays.",
                    "The linked crossword, word-search, or jigsaw puzzle is not deleted.",
                    "This action cannot be undone.",
                ]}
                callback={handleConfirmDelete}
            />

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded. Linked jigsaw and word puzzles for this story are disabled too."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This sliding puzzle"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmDisable}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />

            <StoryImageZoomDialog
                open={Boolean(zoomImage?.url)}
                imageUrl={zoomImage?.url || ""}
                title={zoomImage?.title}
                onClose={() => !regenLoading && !deleting && !disabling && setZoomImage(null)}
                onDelete={zoomImage?.storyId ? () => setDeleteDialogOpen(true) : undefined}
                deleting={deleting}
                onDisable={
                    zoomImage?.storyId && String(zoomImage?.state || "").toUpperCase() === "PUBLISHED"
                        ? () => setDisableDialogOpen(true)
                        : undefined
                }
                disabling={disabling}
                onRegen={zoomImage?.storyId ? () => setRegenDialogOpen(true) : undefined}
                regenLoading={regenLoading}
                free={zoomImage?.free === true}
                onFreeChange={
                    zoomImage?.storyId
                        ? (checked) => handleStoryFreeChange(zoomImage.storyId, checked)
                        : undefined
                }
                savingFree={savingFreeStoryId === zoomImage?.storyId}
                state={zoomImage?.state}
                standalone={zoomImage?.standalone === true}
                onPublish={
                    zoomImage?.storyId
                        ? () => handlePublishStory(zoomImage.storyId, zoomImage.templates)
                        : undefined
                }
                publishing={publishingStoryId === zoomImage?.storyId}
                extra={
                    zoomImage?.storyId ? (
                        <SlidingPlaySummaryRow storyId={zoomImage.storyId} />
                    ) : null
                }
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
